import React, { useEffect, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { BsEnvelope, BsGeoAlt, BsTelephone, BsWhatsapp } from "react-icons/bs";

const antIcon = <LoadingOutlined style={{ fontSize: 22 }} spin />;

const ContactUs = () => {
  const [form, setForm] = useState({ name: "", email: "", contact: "", message: "" });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
    setStatus("");
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Please enter your name.";
    if (!form.email.trim()) nextErrors.email = "Please enter your email address.";
    else if (!/\S+@\S+\.\S+/.test(form.email)) nextErrors.email = "Enter a valid email address.";
    if (!form.contact.trim()) nextErrors.contact = "Please enter your contact number.";
    else if (!/^\d{10}$/.test(form.contact)) nextErrors.contact = "Enter a valid 10-digit number.";
    if (!form.message.trim()) nextErrors.message = "Tell us a little about your project.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setStatus("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_HAPS_MEDIA_BASE_URL}customer/contact-us/query`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.name.trim(),
            contact_number: form.contact,
            email_id: form.email.trim(),
            message: form.message.trim(),
            status: "1",
          }),
        }
      );
      const result = await response.json();
      if (!response.ok || result.status !== 200) throw new Error("Unable to submit inquiry");

      setForm({ name: "", email: "", contact: "", message: "" });
      setStatus("success");
    } catch (error) {
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const fieldClass =
    "mt-2 w-full border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-[#858653] focus:ring-1 focus:ring-[#858653]";

  return (
    <main className="bg-[#f5f3f1] text-gray-900">
      <section className="site-container grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="flex flex-col justify-center bg-[#20211d] px-5 py-12 text-white sm:px-10 md:py-16 lg:px-16">
          <p className="text-xs font-bold uppercase text-[#c49a8c]">Contact Fina Interiors</p>
          <h1 className="mt-3 text-4xl font-semibold leading-tight md:text-5xl">
            Let us talk about your space
          </h1>
          <p className="mt-5 max-w-xl text-base leading-7 text-gray-300">
            Share your requirements, timeline and ideas. Our Pune team will help you
            identify the right design or furniture solution.
          </p>

          <address className="mt-9 space-y-5 not-italic">
            <div className="flex gap-3">
              <BsGeoAlt className="mt-1 shrink-0 text-[#a6a76c]" size={20} aria-hidden="true" />
              <p className="max-w-md text-sm leading-6 text-gray-300">
                Shop No. 1, Apartment, Mohammed Wadi Rd, near Desai Hospital,
                opposite Margosa Heights, Autadwadi Handewadi, Hadapsar, Pune,
                Maharashtra 411060
              </p>
            </div>
            <a href="tel:+919112881916" className="flex items-center gap-3 text-sm text-gray-200 hover:text-white">
              <BsTelephone className="text-[#a6a76c]" size={19} aria-hidden="true" />
              +91 91128 81916
            </a>
            <a href="mailto:finainterior29@gmail.com" className="flex items-center gap-3 text-sm text-gray-200 hover:text-white">
              <BsEnvelope className="text-[#a6a76c]" size={19} aria-hidden="true" />
              finainterior29@gmail.com
            </a>
          </address>

          <a
            href="https://wa.me/919112881916?text=Hi%20Fina%20Interiors%2C%20I%20would%20like%20to%20discuss%20my%20interior%20project."
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex w-fit items-center gap-2 bg-[#858653] px-5 py-3 text-sm font-semibold hover:bg-[#747548]"
          >
            <BsWhatsapp size={18} aria-hidden="true" />
            Chat on WhatsApp
          </a>
        </div>

        <div className="bg-white px-5 py-12 sm:px-10 md:py-16 lg:px-16">
          <div className="mx-auto max-w-xl">
            <p className="text-xs font-bold uppercase text-[#858653]">Project inquiry</p>
            <h2 className="mt-2 text-3xl font-semibold">Tell us what you are planning</h2>
            <form onSubmit={sendMessage} className="mt-8 space-y-5" noValidate>
              <div>
                <label htmlFor="contact-name" className="text-sm font-medium">Name</label>
                <input id="contact-name" name="name" value={form.name} onChange={updateField} className={fieldClass} autoComplete="name" />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-email" className="text-sm font-medium">Email address</label>
                  <input id="contact-email" name="email" type="email" value={form.email} onChange={updateField} className={fieldClass} autoComplete="email" />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="contact-phone" className="text-sm font-medium">Phone number</label>
                  <input id="contact-phone" name="contact" type="tel" inputMode="numeric" value={form.contact} onChange={updateField} className={fieldClass} autoComplete="tel" maxLength={10} />
                  {errors.contact && <p className="mt-1 text-xs text-red-600">{errors.contact}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className="text-sm font-medium">Your project</label>
                <textarea id="contact-message" name="message" rows={6} value={form.message} onChange={updateField} className={fieldClass} placeholder="Tell us about your space, location and timeline." />
                {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
              </div>

              {status === "success" && (
                <p className="border-l-2 border-[#858653] bg-[#f5f3f1] px-4 py-3 text-sm text-gray-700" role="status">
                  Thank you. Your inquiry has been submitted and our team will contact you shortly.
                </p>
              )}
              {status === "error" && (
                <p className="border-l-2 border-red-600 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                  We could not submit your inquiry. Please try again or contact us on WhatsApp.
                </p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex min-h-[46px] w-full items-center justify-center bg-[#20211d] px-6 py-3 text-sm font-semibold text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {isLoading ? <Spin indicator={antIcon} /> : "Send inquiry"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="site-container py-5 md:py-10">
        <div
          className="h-[42vh] min-h-[240px] max-h-[420px] bg-cover bg-center lg:h-[50vh] lg:min-h-[300px] lg:max-h-[520px]"
          style={{
            backgroundImage: "url('/IMG_4975.png')",
            backgroundPosition: "50% 100%",
            backgroundSize: "300% 200%",
          }}
          role="img"
          aria-label="Fina Interiors dining room design"
        />
      </section>
    </main>
  );
};

export default ContactUs;
